import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { AppStateService } from '../app-state/app-state.service';
import { API_BASE, PROJECT } from '../../constants/general.constants';
import { ChunkContext } from '../../interfaces/chunk/chunk-context.interface';
import type { StepParams } from '@enlighten/ts-shared/step-api';

/**
 * A project state entry as the server returns it (`ProjectState` in ehq_core's
 * `models/project_state.rs`, camelCase on the wire). Timestamps are the
 * server's naive UTC datetimes; the `*By*` ids are null when no job or user
 * made the change.
 */
export interface ProjectState {
  projectId: number;
  key: string;
  value: unknown;
  version: number;
  createdAt: string;
  createdByJobId: number | null;
  createdByUserId: number | null;
  updatedAt: string;
  updatedByJobId: number | null;
  updatedByUserId: number | null;
}

/** One entry of `listState()`: the metadata, without the value (`ProjectStateMetadata` on the server). */
export interface ProjectStateMetadata {
  key: string;
  version: number;
  updatedAt: string;
  updatedByJobId: number | null;
  updatedByUserId: number | null;
}

@Injectable({
  providedIn: 'root',
})
export class ProjectStateService {
  readonly #http = inject(HttpClient);
  readonly #appState = inject(AppStateService);

  private buildUrl(key?: string): string {
    const projectId = this.#appState.projectId();
    const base = `${API_BASE}/${PROJECT}/${projectId}/state`;
    return key ? `${base}/${encodeURIComponent(key)}` : base;
  }

  // The methods below take the params the shared parsers return (EHQ-10): the
  // key is trimmed and the version checked before the call reaches here.
  public async getState(
    params: StepParams['EHQ.getState'],
    context?: ChunkContext,
  ): Promise<ProjectState | null> {
    try {
      return await firstValueFrom(this.#http.get<ProjectState>(this.buildUrl(params.key)));
    } catch (error: any) {
      if (error?.status === 404) { return null; }
      const msg = `Error in getState: ${error.message || error}`;
      if (context) { context.addMessage(msg, 'danger'); context.setChunkResultStatus('error'); }
      throw error;
    }
  }

  public async setState(params: StepParams['EHQ.setState'], context?: ChunkContext): Promise<ProjectState> {
    const { key, value, version } = params;
    try {
      return await firstValueFrom(
        this.#http.put<ProjectState>(this.buildUrl(key), { value, version })
      );
    } catch (error: any) {
      const msg = formatStateError('setState', error, { key, version });
      if (context) { context.addMessage(msg, 'danger'); context.setChunkResultStatus('error'); }
      throw new Error(msg);
    }
  }

  public async listState(
    _params: StepParams['EHQ.listState'],
    context?: ChunkContext,
  ): Promise<ProjectStateMetadata[]> {
    try {
      return await firstValueFrom(this.#http.get<ProjectStateMetadata[]>(this.buildUrl()));
    } catch (error: any) {
      const msg = `Error in listState: ${error.message || error}`;
      if (context) { context.addMessage(msg, 'danger'); context.setChunkResultStatus('error'); }
      throw error;
    }
  }

  public async deleteState(params: StepParams['EHQ.deleteState'], context?: ChunkContext): Promise<boolean> {
    const { key, version } = params;
    try {
      await firstValueFrom(this.#http.delete(this.buildUrl(key), {
        params: new HttpParams().set('version', version.toString()),
      }));
      return true;
    } catch (error: any) {
      const msg = formatStateError('deleteState', error, { key, version });
      if (context) { context.addMessage(msg, 'danger'); context.setChunkResultStatus('error'); }
      throw new Error(msg);
    }
  }
}

/**
 * Format state-service errors so the user gets an actionable message instead
 * of Angular's default "Http failure response for <url>: 409 OK" string.
 *
 * Mirrors enoch/root/src/services/state.service.ts which already does
 * status-specific formatting for 409 conflicts.
 *
 * SCRUM-494: surfaces "Version conflict ... use getState() to fetch the
 * current version first" so QA / authors immediately understand the OCC
 * contract instead of decoding Angular's misleading "409 OK" wording.
 */
function formatStateError(
  op: 'setState' | 'deleteState',
  error: any,
  ctx: { key?: string; version?: number },
): string {
  const status: number | undefined = error?.status;
  const keyLabel = ctx.key ? `"${ctx.key}"` : '<unknown key>';
  const serverBody = extractServerErrorText(error);

  if (status === 409) {
    const expected = typeof ctx.version === 'number' ? ` (expected version ${ctx.version})` : '';
    const hint = ctx.version === 0
      ? 'A state entry with that key already exists. Call getState() first, then setState(key, value, currentVersion).'
      : 'The entry was modified by another writer since you last read it. Re-read with getState() and retry.';
    const bodySuffix = serverBody ? ` Server: ${serverBody}.` : '';
    return `Version conflict in ${op} for ${keyLabel}${expected}.${bodySuffix} ${hint}`;
  }

  if (status === 404 && op === 'deleteState') {
    return `State key ${keyLabel} not found (deleteState).`;
  }

  const baseMessage = (error && (error.message || String(error))) || 'unknown error';
  const serverSuffix = serverBody && !baseMessage.includes(serverBody) ? ` Server: ${serverBody}.` : '';
  return `Error in ${op}: ${baseMessage}.${serverSuffix}`;
}

function extractServerErrorText(error: any): string | null {
  if (!error || typeof error !== 'object') return null;
  const body = (error as { error?: unknown }).error;
  if (!body) return null;
  if (typeof body === 'string') return body.trim() || null;
  if (typeof body === 'object') {
    const obj = body as Record<string, unknown>;
    const candidate =
      (typeof obj['error'] === 'string' && obj['error']) ||
      (typeof obj['message'] === 'string' && obj['message']) ||
      (typeof obj['detail'] === 'string' && obj['detail']);
    if (candidate) return candidate;
    try {
      return JSON.stringify(obj);
    } catch {
      return null;
    }
  }
  return null;
}
